// @givmo/board — the board-visible comments on an issued packet version, from the officer's side.
//
// ══ THIS LIST IS NOT "ALL COMMENTS", AND THE COPY SAYS SO ═══════════════════════════════════════
//
// A director's PRIVATE note is unreachable through this read — the filter is applied in SQL and takes
// no role into account, so it binds every administrator of the organization including an Owner. That is the whole
// value of the feature, and the officer is told about it rather than left to infer that silence here
// means nobody wrote anything. Saying "directors can also keep private notes" discloses no note and no
// author; it just stops an officer treating this as a complete picture.
//
// ══ THE ON-BEHALF PATH IS BOARD-VISIBLE ONLY, BY CONSTRUCTION ═══════════════════════════════════
//
// An officer can enter a comment a director made by telephone or in person. They CANNOT enter a private
// note, and no shape here could: the command family has no such option, the wire model validates
// `visibility == board`, and a database CHECK refuses the combination. So this panel offers exactly one
// kind of entry, and the seat picker names whose words they are — an officer's own contribution to a
// packet is a NOTE ENTRY in the packet itself, which is packet contents rather than someone else's
// comment.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Select,
  Skeleton,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { extractBoardMembers, isCommitteeOnlySeat } from '../roster/roster-decode';
import {
  displayInstant,
  extractAnnotations,
  recordedByLabel,
  sharedComments,
} from './packets-decode';
import { useBoardOperations } from '../../board-runtime';
import { useBoardPermission } from '../../board-host';
import { committeeInviteeSeatIds } from '../committees/committees-decode';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

export interface SharedCommentsPanelProps {
  orgRef: string;
  packetId: string;
  /** CBG-YR6-MEET: a committee meeting's packet is read by the committee's members. */
  committeeName?: string | null;
  /** The committee whose packet it is (null: the board's) — whose members alone a comment is
   *  entered for (CBG-YR6-OUTSIDE §5.2, review-215's F3). */
  committeeId?: string | null;
}

export function SharedCommentsPanel({
  orgRef,
  packetId,
  committeeName = null,
  committeeId = null,
}: SharedCommentsPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  // Entering a comment on a director's behalf is `board_meeting:write`. Without it the form is
  // absent rather than a set of disabled fields; the packet page states the line once.
  const canRecord = useBoardPermission('board_meeting:write');
  const [seatId, setSeatId] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const commentsOptions = operations.officerListBoardPacketCommentsOptions({
    path: { org_ref: orgRef, packet_id: packetId },
  });
  const commentsQuery = useQuery({ ...commentsOptions, select: extractAnnotations });
  // Read positively even though this route returns board-visible rows only: a second filter here costs
  // nothing and means a widened backend read can never surface a private note through this panel.
  const comments = sharedComments(commentsQuery.data ?? []);

  const seatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    select: extractBoardMembers,
  });
  const committeeQuery = useQuery({
    ...operations.officerGetBoardCommitteeOptions({
      path: { org_ref: orgRef, committee_id: committeeId ?? '' },
    }),
    enabled: committeeId !== null,
  });
  const seats = seatsQuery.data ?? [];
  // Someone the packet went to (CBG-YR6-OUTSIDE §5.2): the board's packet, the board's seats; a
  // committee's, its members, none until the committee is read — the backend's rule, which answers
  // anyone else "Board member not found".
  const committeeSeats =
    committeeId === null
      ? null
      : committeeQuery.data
        ? committeeInviteeSeatIds(committeeQuery.data)
        : new Set<string>();
  const activeSeats = seats.filter(
    (seat) =>
      seat.status === 'active' &&
      (committeeSeats === null ? !isCommitteeOnlySeat(seat) : committeeSeats.has(seat.id)),
  );
  const seatNames = new Map(seats.map((seat) => [seat.id, seat.display_name]));

  const record = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardPacketSharedComment({
        path: { org_ref: orgRef, packet_id: packetId },
        body: {
          board_member_id: seatId ?? '',
          body: body.trim(),
          // Board-visible is the only value this path accepts, and it is stated rather than defaulted.
          visibility: 'board',
        },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setBody('');
      setSeatId(null);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: commentsOptions.queryKey });
      toast.success('Comment recorded.');
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  return (
    <Card testId="packet-comments">
      <CardBody className="flex flex-col gap-16">
        <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
          Comments on this version
        </h2>

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {committeeName !== null
            ? cm.packetCommentReaders(committeeName)
            : 'These are the comments every director on this board can read. Directors can also keep private notes on a packet — those are theirs alone and nobody else sees them, including you.'}
        </p>

        {commentsQuery.isPending ? (
          <Skeleton className="h-48 w-full rounded-lg" testId="packet-comments-loading" />
        ) : null}

        {commentsQuery.isSuccess && comments.length === 0 ? (
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp('packet-comments-empty')}
          >
            No board-visible comments on this version.
          </p>
        ) : null}

        {comments.length > 0 ? (
          <ul className="flex flex-col gap-8" data-testid="packet-comment-list">
            {comments.map((comment) => {
              const onBehalf = recordedByLabel(comment.recorded_by);
              return (
                <li
                  key={comment.id}
                  className="flex flex-col gap-4 rounded-lg border border-border-subtle p-12"
                  data-testid={`packet-comment-${comment.id}`}
                >
                  <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    {comment.body}
                  </span>
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                    {seatNames.get(comment.board_member_id) ?? 'A seat on this board'} ·{' '}
                    {displayInstant(comment.created_at)}
                    {onBehalf !== null ? ` · ${onBehalf}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}

        {canRecord ? (
          <div className="flex flex-col gap-12 border-t border-border-subtle pt-16">
            <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              Write down a comment for someone
            </h3>

            <Select
              label="Whose comment it is"
              appearance="saas"
              value={seatId ?? ''}
              onValueChange={(next) => setSeatId(next !== null && next.length > 0 ? next : null)}
              options={[
                { value: '', label: 'Choose a board member…' },
                ...activeSeats.map((seat) => ({ value: seat.id, label: seat.display_name })),
              ]}
              testId="packet-comment-seat"
            />
            <FieldNote>
              For a comment they gave you by phone or in person. It goes in as theirs, marked as
              entered by you.
            </FieldNote>

            <Textarea
              label="What they said"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={3}
              testId="packet-comment-body"
            />

            {error !== null ? (
              <ErrorMessage testId="packet-comment-error">{error}</ErrorMessage>
            ) : null}

            <Button
              appearance="saas"
              tone="secondary"
              className="self-start"
              loading={record.isPending}
              disabled={seatId === null || body.trim().length === 0 || record.isPending}
              onClick={() => {
                setError(null);
                record.mutate();
              }}
              testId="packet-comment-save"
            >
              Record it
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
